import { Stat, TxCableCard } from "./primitives";

/* v2.87 · Conductor sizing result panel — extracted from Calculators.jsx.
   Renders the base stat grid + optional sub-panels for conduit, motor
   branch, transformer (both-sides), and motor group feeder. */
export function ConductorResultPanel({ result }) {
  return (
    <div className="mt-6 border border-[#00E5FF]/30 bg-[#0A0C10] p-4 font-mono text-sm grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="cond-result">
      <Stat label="Selected size"
            value={`${result.selected_size} AWG/kcmil`}
            accent
            data-testid="cond-selected-size" />
      <Stat label="Driven by"
            value={{
              voltage_drop: "voltage drop",
              breaker_protection: "breaker (NEC 240.4)",
              ampacity: "ampacity",
            }[result.selection_driven_by] || result.selection_driven_by}
            accent={result.selection_driven_by !== "ampacity"}
            data-testid="cond-driven-by" />
      <Stat label="Ampacity (table)" value={`${result.table_ampacity} A`} />
      <Stat label="Derated" value={`${result.derated_ampacity} A`} />
      <Stat label="Required" value={`${result.required_ampacity} A`} />
      <Stat label="Breaker" value={`${result.recommended_breaker} A`} accent />
      {result.total_derated_ampacity != null && result.input.parallel_sets > 1 && (
        <Stat label="Total cable ampacity"
              value={`${result.total_derated_ampacity} A (${result.input.parallel_sets}× ${result.derated_ampacity})`}
              accent={result.breaker_protects_cable}
              warn={result.breaker_protects_cable === false}
              data-testid="cond-total-ampacity" />
      )}
      <Stat label="Voltage drop" value={`${result.voltage_drop_pct}% (${result.voltage_drop_volts}V)`} warn={!result.voltage_drop_compliant} />
      {result.voltage_drop_model && (
        <div className="col-span-full text-[10px] text-gray-400 -mt-2" data-testid="cond-vd-model">
          <span className="text-gray-500">// VD model:</span> {result.voltage_drop_model}
        </div>
      )}
      <Stat label="Ambient factor" value={(result.ambient_factor ?? 1).toFixed(2)} />
      <Stat label="Media factor"
            value={(result.media_factor ?? 1).toFixed(2)}
            accent={result.media_factor != null && result.media_factor < 1}
            data-testid="cond-media-factor" />
      <Stat label="Combined derate"
            value={(result.combined_factor ?? (result.ambient_factor || 1)).toFixed(2)}
            warn={result.combined_factor != null && result.combined_factor < 0.5} />
      {result.ocpd_exception_used && (
        <div className="col-span-full p-2 border border-[#FFCC00]/40 bg-[#FFCC00]/10 text-[#FFCC00] text-xs" data-testid="cond-ocpd-exception">
          <strong>NEC 240.4(B) applied</strong> — cable ampacity ({result.total_derated_ampacity} A) is below the {result.recommended_breaker} A breaker, but the exception permits the next standard OCPD above the cable rating for feeders ≤ 800 A. Confirm this circuit is not a multi-receptacle branch circuit.
        </div>
      )}
      {result.media_citation && (
        <div className="col-span-full text-[10px] text-gray-400" data-testid="cond-media-citation">
          <span className="text-gray-500">// applied media rule:</span> {result.media_citation}
        </div>
      )}
      {result.wet_warning && (
        <div className="col-span-full p-2 border border-[#FF3B30]/40 bg-[#FF3B30]/10 text-[#FF8A85] text-xs" data-testid="cond-wet-warning">
          ⚠ {result.wet_warning}
        </div>
      )}
      {result.conduit?.supported && (
        <div className="col-span-full mt-2 border border-[#00E5FF]/20 bg-[#00E5FF]/5 p-3" data-testid="cond-conduit-result">
          <div className="label-tag mb-2">// conduit fill · NEC Ch 9 Table 4 + 5</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label={`${result.conduit.material} trade size`}
                  value={`${result.conduit.trade_size}"`}
                  accent
                  warn={result.conduit.over_limit}
                  data-testid="cond-conduit-trade" />
            <Stat label="Fill used"
                  value={`${result.conduit.fill_pct}% / ${result.conduit.fill_limit_pct}%`}
                  warn={result.conduit.over_limit} />
            <Stat label="EGC (250.122)"
                  value={`${result.conduit.per_raceway.egc_size} AWG`} />
            <Stat label="Raceways needed"
                  value={`${result.conduit.raceways_required}`}
                  accent={result.conduit.raceways_required > 1} />
          </div>
          <div className="text-[10px] text-gray-400 mt-2">
            Per raceway: {result.conduit.per_raceway.phase_conductors} phase{result.conduit.per_raceway.neutral ? " + 1 neutral" : ""} + 1 EGC ({result.conduit.per_raceway.total_wire_count} wires total).
            {result.conduit.over_limit && (
              <span className="text-[#FF8A85] ml-1">⚠ Exceeds fill limit — use parallel raceways or a larger material.</span>
            )}
          </div>
        </div>
      )}
      {result.conduit && !result.conduit.supported && (
        <div className="col-span-full p-2 border border-white/10 bg-white/5 text-xs text-gray-400" data-testid="cond-conduit-skipped">
          Conduit sizing skipped — {result.conduit.reason}
        </div>
      )}
      {result.motor_branch && (
        <div className="col-span-full mt-2 border border-[#00E5FF]/20 bg-[#00E5FF]/5 p-3" data-testid="cond-motor-branch-result">
          <div className="label-tag mb-2">// motor branch · NEC 430.52 + 430.32</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Motor FLC"
                  value={`${result.motor_branch.fla} A`}
                  data-testid="cond-mb-fla" />
            <Stat label={`OCPD (${result.motor_branch.max_ocpd_pct}% max)`}
                  value={`${result.motor_branch.recommended_ocpd_amps} A`}
                  accent
                  data-testid="cond-mb-ocpd" />
            <Stat label={`OL relay setpoint (${result.motor_branch.overload.setpoint_pct}%)`}
                  value={`${result.motor_branch.overload.setpoint_amps} A`}
                  data-testid="cond-mb-ol" />
            <Stat label={`OL relay hard max (${result.motor_branch.overload.hard_max_pct}%)`}
                  value={`${result.motor_branch.overload.hard_max_amps} A`} />
          </div>
          <div className="text-[10px] text-gray-400 mt-2">
            {result.motor_branch.protection_label} · max = {result.motor_branch.max_ocpd_amps} A rounded up per 430.52(C)(1) to {result.motor_branch.recommended_ocpd_amps} A · overload set 115–125 % of FLC (NEC 430.32(A), hard max 130–140 % per 430.32(C)).
          </div>
        </div>
      )}
      {result.transformer && (
        <div className="col-span-full mt-2 border border-[#00E5FF]/20 bg-[#00E5FF]/5 p-3" data-testid="cond-transformer-result">
          <div className="label-tag mb-2">// transformer · NEC 450.3(B)</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label={`Primary FLC (${result.transformer.primary.voltage} V)`}
                  value={`${result.transformer.primary.fla} A`}
                  data-testid="cond-tx-pri-fla" />
            <Stat label={`Primary OCPD (${result.transformer.primary.max_ocpd_pct}% max)`}
                  value={`${result.transformer.primary.recommended_ocpd_amps} A`}
                  accent={result.input.transformer_side !== "secondary"}
                  data-testid="cond-tx-pri-ocpd" />
            {result.transformer.secondary && (
              <>
                <Stat label={`Secondary FLC (${result.transformer.secondary.voltage} V)`}
                      value={`${result.transformer.secondary.fla} A`}
                      data-testid="cond-tx-sec-fla" />
                <Stat label={`Secondary OCPD (${result.transformer.secondary.max_ocpd_pct}% max)`}
                      value={`${result.transformer.secondary.recommended_ocpd_amps} A`}
                      accent={result.input.transformer_side === "secondary"}
                      data-testid="cond-tx-sec-ocpd" />
              </>
            )}
          </div>
          <div className="text-[10px] text-gray-400 mt-2">
            Primary rule: {result.transformer.primary.rule}. Conductor on this side must carry ≥ {(result.input.transformer_side === "secondary" && result.transformer.secondary?.conductor_min_amps) || result.transformer.primary.conductor_min_amps} A (NEC 450.3 & 240.21(C), 125 % of FLC).
            {result.transformer.supervised && <span className="text-[#FFCC00]"> · <b>Supervised location exception applied.</b></span>}
          </div>
          {result.transformer.size_both_sides && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="cond-tx-both-sides">
              <TxCableCard side="primary" cable={result.transformer.primary_cable}
                fla={result.transformer.primary.fla}
                ocpd_pct={result.transformer.primary.max_ocpd_pct} />
              {result.transformer.secondary_cable && (
                <TxCableCard side="secondary" cable={result.transformer.secondary_cable}
                  fla={result.transformer.secondary.fla}
                  ocpd_pct={result.transformer.secondary.max_ocpd_pct} />
              )}
            </div>
          )}
        </div>
      )}
      {result.motor_group?.supported && (
        <div className="col-span-full mt-2 border border-[#00E5FF]/20 bg-[#00E5FF]/5 p-3" data-testid="cond-motor-group-result">
          <div className="label-tag mb-2">// motor group feeder · NEC 430.24 + 430.62</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Feeder ampacity min"
                  value={`${result.motor_group.feeder_ampacity_min} A`}
                  accent
                  data-testid="cond-mg-ampacity" />
            <Stat label="Feeder OCPD"
                  value={`${result.motor_group.feeder_ocpd} A`}
                  accent
                  data-testid="cond-mg-ocpd" />
            <Stat label="Largest motor FLC"
                  value={`${result.motor_group.largest_motor_fla} A × 1.25`} />
            <Stat label="Σ other FLCs"
                  value={`${result.motor_group.other_motor_flas_total} A`} />
          </div>
          <div className="mt-3 text-[11px] font-mono text-gray-300">
            <span className="text-gray-500">// motor list:</span>
            <ul className="mt-1 space-y-0.5">
              {result.motor_group.motors.map((m, i) => (
                <li key={i} className={m.is_largest ? "text-[#00E5FF]" : ""}>
                  {m.is_largest ? "★" : "·"} {m.tag || `M-${i+1}`}
                  {m.hp != null ? ` · ${m.hp} hp` : ""}
                  {` · ${m.fla} A${m.voltage ? ` @ ${m.voltage} V` : ""}${m.phases ? ` ${m.phases}φ` : ""}`}
                </li>
              ))}
            </ul>
          </div>
          <div className="text-[10px] text-gray-400 mt-2">
            {result.motor_group.reference}
          </div>
        </div>
      )}
      <div className="col-span-full text-gray-500 text-xs mt-2">
        <strong className="text-gray-300">Cable:</strong> {result.input.cable_type}
        {result.cable_info?.desc ? ` — ${result.cable_info.desc}` : ""}<br />
        <strong className="text-gray-300">Media:</strong> {result.input.media} ·
        <strong className="text-gray-300 ml-1">Cables in media:</strong> {result.input.cables_in_media ?? "—"} ·
        <strong className="text-gray-300 ml-1">{result.input.bundled ? "Bundled" : "Spaced ≥ 1 OD"}</strong><br />
        <strong className="text-gray-300">Ref:</strong> {result.reference}<br />
        {result.notes}
      </div>
    </div>
  );
}
